import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, type Tournament, type TournamentDivision } from "../lib/api";
import { divisionAccentStyle, firstActiveDivisionByName } from "../lib/tournamentDivisions";

const TOURNAMENT_PATH_RE = /^\/admin\/tournaments\/(\d+)(?:\/|$)/;

function tournamentIdFromPath(pathname: string): number | null {
  const match = pathname.match(TOURNAMENT_PATH_RE);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function selectedDivisionFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get("division");
  if (!raw) return null;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export default function AdminTournamentContextNav() {
  const { token, status } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tournamentId = tournamentIdFromPath(location.pathname);
  const selectedDivisionId = selectedDivisionFromSearch(location.search);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [loadedTournamentId, setLoadedTournamentId] = useState<number | null>(null);
  const [contextState, setContextState] = useState<"loading" | "loaded" | "failed">("loading");

  useEffect(() => {
    let cancelled = false;
    async function loadContext() {
      if (!token || !tournamentId || status !== "authenticated") {
        setTournament(null);
        setTournaments([]);
        setDivisions([]);
        setLoadedTournamentId(null);
        return;
      }
      setContextState("loading");
      try {
        const [tournamentResponse, divisionsResponse, tournamentsResponse] = await Promise.all([
          api.tournaments.get(token, tournamentId),
          api.tournaments.listDivisions(token, tournamentId),
          api.tournaments.listMine(token),
        ]);
        if (cancelled) return;
        setTournament(tournamentResponse.data.tournament);
        setTournaments(
          [...tournamentsResponse.data.tournaments].sort((left, right) =>
            left.name.localeCompare(right.name, "es", { sensitivity: "base" }),
          ),
        );
        setDivisions(divisionsResponse.data.divisions);
        setLoadedTournamentId(tournamentId);
        setContextState("loaded");
      } catch {
        if (cancelled) return;
        setTournament(null);
        setTournaments([]);
        setDivisions([]);
        setLoadedTournamentId(null);
        setContextState("failed");
      }
    }
    void loadContext();
    return () => {
      cancelled = true;
    };
  }, [status, token, tournamentId]);

  // Las categorías DESACTIVADAS no se ofrecen en el selector (pedido de
  // Eduardo 2026-08-14): solo se conserva la actualmente seleccionada si
  // quedó inactiva, para no romper un link profundo — marcada como tal.
  const sortedDivisions = useMemo(() => {
    const visibles = divisions.filter(
      (division) => division.is_active !== false || division.id === selectedDivisionId,
    );
    return [...visibles].sort((left, right) => left.name.localeCompare(right.name, "es", { sensitivity: "base" }));
  }, [divisions, selectedDivisionId]);

  useEffect(() => {
    if (!tournamentId || loadedTournamentId !== tournamentId || selectedDivisionId) return;
    // Misma regla que usa Grupos mientras esta barra carga (Eduardo 2026-10-05).
    const firstDivision = firstActiveDivisionByName(divisions);
    if (!firstDivision) return;
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("division", String(firstDivision.id));
    // Se conserva el estado de la navegación: el aviso «Este torneo sigue en
    // borrador» (TournamentSetupGuard) viaja ahí y se perdía al completar la
    // categoría (smoke de #431, torneo 59).
    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true, state: location.state });
  }, [divisions, loadedTournamentId, location.pathname, location.search, location.state, navigate, selectedDivisionId, tournamentId]);

  const activeDivision = useMemo(() => {
    if (sortedDivisions.length === 0) return null;
    return sortedDivisions.find((division) => division.id === selectedDivisionId) ?? sortedDivisions[0] ?? null;
  }, [selectedDivisionId, sortedDivisions]);

  if (!tournamentId) return null;

  // Mientras cargan los fetches, la barra reserva su lugar con un esqueleto:
  // si aparece de golpe ya cargada, es sticky y EMPUJA todo el contenido
  // hacia abajo cientos de ms después del primer paint — los botones se
  // movían bajo el cursor (QA Cowork #12). Si el fetch falla, se colapsa
  // como siempre (useFixedChromeHeights ya vigila ese desmonte).
  if (!tournament) {
    if (contextState === "failed") return null;
    return (
      <nav className="admin-tournament-context-nav" data-admin-tournament-context-nav="true" aria-label="Torneo activo" aria-busy="true">
        <div className="admin-tournament-context-nav__desktop-row">
          <div className="admin-tournament-context-nav__tournament-row">
            <label className="admin-tournament-context-nav__tournament-select-wrap">
              <span className="admin-tournament-context-nav__eyebrow">Torneo seleccionado</span>
              <select className="admin-tournament-context-nav__tournament-select" disabled aria-label="Cargando torneo">
                <option>Cargando…</option>
              </select>
            </label>
          </div>
        </div>
      </nav>
    );
  }

  const activeStyle = activeDivision ? (divisionAccentStyle(activeDivision) as CSSProperties) : undefined;

  // Feedback de Eduardo (2026-07-14): Datos, Reglamento, Convocatoria,
  // Inscripciones y Comunicación aplican al torneo COMPLETO — mostrar ahí el
  // selector de categoría confunde. Solo se oculta el dropdown; la división
  // del query se conserva para cuando regresen a un paso por categoría.
  const GENERAL_SECTION_RE = /\/(edit|setup\/(rules|call|registrations|settings)|rules|communications|announcements|registrations)(\/|$)/;
  const hideCategorySelect = GENERAL_SECTION_RE.test(location.pathname);

  function handleSelectDivision(divisionId: number) {
    if (!tournament) return;
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("division", String(divisionId));
    const targetPath = location.pathname === `/admin/tournaments/${tournament.id}`
      ? `/admin/tournaments/${tournament.id}/players`
      : location.pathname;
    navigate(`${targetPath}?${nextParams.toString()}`, { replace: false });
  }

  function handleSelectTournament(nextTournamentId: number) {
    if (!tournament || !Number.isFinite(nextTournamentId) || nextTournamentId <= 0 || nextTournamentId === tournament.id) return;
    const currentBase = `/admin/tournaments/${tournament.id}`;
    const currentSuffix = location.pathname.startsWith(`${currentBase}/`)
      ? location.pathname.slice(currentBase.length)
      : "";
    navigate(`/admin/tournaments/${nextTournamentId}${currentSuffix}`);
  }

  return (
    <nav className="admin-tournament-context-nav" data-admin-tournament-context-nav="true" aria-label="Torneo activo">
      <div className="admin-tournament-context-nav__desktop-row">
        <div className="admin-tournament-context-nav__tournament-row">
          <label className="admin-tournament-context-nav__tournament-select-wrap">
            <span className="admin-tournament-context-nav__eyebrow">Torneo seleccionado</span>
            <select
              className="admin-tournament-context-nav__tournament-select"
              value={tournament.id}
              onChange={(event) => handleSelectTournament(Number(event.target.value))}
              aria-label="Cambiar torneo"
            >
              {(tournaments.length > 0 ? tournaments : [tournament]).map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </label>
        </div>

        {sortedDivisions.length > 0 && !hideCategorySelect && (
          <div className="admin-tournament-context-nav__category-row" style={activeStyle}>
            <label className="admin-tournament-context-nav__category-select-wrap">
              <span className="admin-tournament-context-nav__eyebrow">Categoría seleccionada</span>
              <select
                className="admin-tournament-context-nav__category-select"
                value={activeDivision?.id ?? sortedDivisions[0].id}
                onChange={(event) => handleSelectDivision(Number(event.target.value))}
                aria-label="Cambiar categoría del torneo"
              >
                {sortedDivisions.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.is_active === false ? `${division.name} (inactiva)` : division.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
    </nav>
  );
}
